import React, { useEffect, useMemo, useContext, useState } from 'react';
import {
  Box,
  Collapse,
  Container,
  Grid,
  IconButton,
  Paper,
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import { useTranslation, useQuery } from '../../shared/hooks';
import * as translations from './intl';
import { Button, RadioButton, ConfirmationModal } from '../../components';
import { PatientForm, PatientCard } from '../PatientSelection/components';
import * as Yup from 'yup';
import { GeneralContext } from '../../shared/contexts/GeneralContext.context';
import CloseIcon from '@mui/icons-material/Close';
import {
  getLanguage,
  getPartnerId,
  getPartnerName,
  getCountryCode,
  getInstanceEnv,
} from '../../shared/constants/globalConstants';

const getStyles = (theme) => ({
  title: {
    fontFamily: 'Publico Headline !important',
    fontSize: '36px !important',
    lineHeight: '60px !important',
    color: theme.palette.text.main,
    paddingBottom: '42px',
    paddingTop: '42px',
  },
  subTitle: {
    fontFamily: 'Publico Headline',
    fontSize: '25px',
    lineHeight: '30px',
    letterSpacing: '0.8px',
    padding: '15px 50px',
    color: theme.palette.text.main,
  },
  containerPaper: {
    [theme.breakpoints.down('md')]: {
      padding: '0px 40px !important',
    },
    padding: '0px 90px !important',
    marginBottom: '20px',
  },
  paperContent: {
    width: '100%',
  },
  container: {
    marginBottom: '100px',
  },
  paddingFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '25px 0px',
    borderTop: '1px solid rgba(229, 229, 229, 1)',
  },
  paddingButton: {
    [theme.breakpoints.down('xs')]: {
      marginTop: '10px',
    },
  },
  marginButtonNew: {
    margin: '20px 0px',
  },
  editButton: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
});

const Space = styled('div')({
  width: '100%',
  height: '25px',
});

function User() {
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = getStyles(theme);
  const query = useQuery();
  const {
    fetchClientAndDependents,
    client,
    dependents,
    refreshClientAndDependents,
    fetchAmosCountry,
    adults,
    userData,
    isLogged,
  } = useContext(GeneralContext);
  const [addNewFM, setAddNewFM] = useState(false);
  const [selectedPatientType, setSelectedPatientType] = useState(null);
  const [addNewAB, setAddNewAB] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const { locale, idiomAmos, idiom } = useTranslation({
    translationsFile: translations,
  });
  const type = query.get('type');
  const clientType = query.get('clientType') || null;
  const clientUuid = query.get('clientUuid') || null;
  const [CL, FM, AB] = ['CL', 'FM', 'AB'];
  const isVideo = useMemo(() => type === 'video', [type]);

  const {
    TITLE,
    SUBTITLE_NEED_HELP,
    NEXT_BUTTON,
    BACK_BUTTON,
    FAMILY_MEMBER,
    PREFFIX_CLIENT,
    ADD_NEW_BUTTON,
    SELECT_PATIENT,
    ADULT_BENEFICIARY,
    CONTINUE_BUTTON,
  } = locale;

  // If user is not registered on AMOS, redirect to lets-start
  useEffect(() => {
    if (isLogged && userData?.isRegisteredAmos === false) {
      // history.push('/lets-start?type=user');
      navigate('/lets-start?type=user');
    }
    if (!isLogged) {
      if (window.location.hostname === import.meta.env.VITE_DOMAIN_GENERIC) {
        navigate('/');
      } else {
        navigate(`/login?lang=${idiom.substr(0, 2)}`);
      }
    }
  }, [isLogged, userData]);

  useEffect(() => {
    fetchAmosCountry(idiomAmos);
  }, [idiomAmos]);

  useEffect(() => {
    fetchClientAndDependents();
  }, [refreshClientAndDependents]);

  const handleCLoseAddNew = () => {
    setAddNewFM(!addNewFM);
  };
  const handleCLoseAddNewAB = () => {
    setAddNewAB(!addNewAB);
  };
  const [isConfirmed, setIsConfirmed] = useState(false);

  const handleSubmit = (values) => {
    window.dataLayer.push({
      subsidiary_name: 'axa_partners',
      business_unit: 'customer_and_solutions',
      business_line: 'travel_solutions',
      digital_business_process: 'assistance_request',
      global_digital_asset: 'doctor_please',
      partner_id: getPartnerId(),
      partner_name: getPartnerName(),
      country: getCountryCode(),
      language: getLanguage(),
      instance_environment: getInstanceEnv(),
      user_logged: 'true',
      page_name: 'user selection',
      event: 'pageview',
    });
  };

  const handleBack = () => {
    navigate('/');
  };

  const validationSchema = Yup.object().shape({
    client: Yup.string().required(SELECT_PATIENT),
    clientData: Yup.string().required(SELECT_PATIENT),
  });

  const getOptions = (client) => {
    const options = [
      {
        label: `${PREFFIX_CLIENT}${client?.name}`,
        value: CL,
      },
      { label: FAMILY_MEMBER, value: FM },
    ];
    if (
      window.location.hostname === import.meta.env.VITE_DOMAIN_TRIPUK ||
      window.location.hostname === import.meta.env.VITE_DOMAIN_GENERIC ||
      window.location.hostname === import.meta.env.VITE_DOMAIN_US ||
      window.location.hostname === import.meta.env.VITE_DOMAIN_US_CHUBB ||
      window.location.hostname === import.meta.env.VITE_DOMAIN_US_METLIFE ||
      window.location.hostname === import.meta.env.VITE_DOMAIN_USTI
    ) {
      options.push({ label: ADULT_BENEFICIARY, value: AB });
    }
    return options;
  };

  return (
    <>
      <Container maxWidth="lg" sx={classes.container}>
        <Grid size={{ xs: 12, md: 12 }}>
          <Box component="p" sx={classes.title}>
            {TITLE}
          </Box>
        </Grid>
        {client && (
          <Grid
            size={{ xs: 12, md: 12 }}
            justifyContent="center"
            className="d-flex"
          >
            <Paper sx={classes.paperContent}>
              <Box component="p" sx={classes.subTitle}>
                {SUBTITLE_NEED_HELP}
              </Box>
              <Container maxWidth="lg" sx={classes.containerPaper}>
                <Formik
                  initialValues={{
                    client: '',
                    clientData: '',
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, setFieldValue, setFieldTouched }) => (
                    <div>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <RadioButton
                          name="client"
                          label=""
                          options={getOptions(client)}
                          onChange={(e) => {
                            setFieldValue('client', e.target.value);
                            setFieldValue(
                              'clientData',
                              e.target.value === CL
                                ? client.id
                                : e.target.value === FM &&
                                    dependents?.length === 1
                                  ? dependents[0].id
                                  : e.target.value === AB && adults
                                    ? adults.id
                                    : ''
                            );
                            setSelectedPatientType(e.target.value);
                            if (
                              e.target.value === AB ||
                              e.target.value === AB
                            ) {
                              setAddNewFM(false);
                              setAddNewAB(false);
                            }
                          }}
                          key={'client'}
                        />
                      </Grid>
                      <Space />
                      <Grid size={{ xs: 12, md: 12 }}>
                        {values.client === CL && (
                          <>
                            <PatientCard payload={client} type={CL} />
                          </>
                        )}
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        {values.client === FM && (
                          <>
                            {dependents.map((el) => (
                              <PatientCard payload={el} key={el.id} type={FM} />
                            ))}
                          </>
                        )}
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        {values.client === AB && adults && (
                          <>
                            <PatientCard payload={adults} type={AB} />
                          </>
                        )}
                      </Grid>
                      {values.client === FM && (
                        <Collapse in={addNewFM}>
                          <Grid
                            size={{ xs: 12, md: 12 }}
                            sx={classes.editButton}
                          >
                            <IconButton
                              aria-label="close-add-new"
                              color="secondary"
                              onClick={handleCLoseAddNew}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Grid>
                          <Grid size={{ xs: 12, md: 12 }}>
                            <PatientForm
                              payload={{
                                relationType: '',
                                clientCode: '',
                                firstName: '',
                                lastName: '',
                                day: '',
                                month: '',
                                year: '',
                                gender: '',
                                email: '',
                                countryCode: '',
                                dependentCode: '',
                                cellphone: '',
                                dependentId: null,
                                countryResidence: '',
                                language: '',
                              }}
                              type={values.client == FM ? FM : AB}
                              handleClose={handleCLoseAddNew}
                            />
                          </Grid>
                        </Collapse>
                      )}
                      {values.client === AB && (
                        <Collapse in={addNewAB}>
                          <Grid
                            size={{ xs: 12, md: 12 }}
                            sx={classes.editButton}
                          >
                            <IconButton
                              aria-label="close-add-new"
                              color="secondary"
                              onClick={handleCLoseAddNewAB}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Grid>
                          <Grid size={{ xs: 12, md: 12 }}>
                            <PatientForm
                              payload={{
                                relationType: '',
                                clientCode: '',
                                firstName: '',
                                lastName: '',
                                day: '',
                                month: '',
                                year: '',
                                gender: '',
                                email: '',
                                countryCode: '',
                                dependentCode: '',
                                cellphone: '',
                                dependentId: null,
                                countryResidence: '',
                                language: '',
                              }}
                              type={values.client == FM ? FM : AB}
                              handleClose={handleCLoseAddNewAB}
                            />
                          </Grid>
                        </Collapse>
                      )}

                      {((values.client === FM && !addNewFM) ||
                        (values.client === AB && adults == null)) && (
                        <Grid size={{ xs: 12, md: 12 }}>
                          <Button
                            text={ADD_NEW_BUTTON}
                            sx={classes.marginButtonNew}
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                              if (values.client === FM) {
                                setAddNewFM(true);
                              }
                              if (values.client === AB) {
                                setAddNewAB(true);
                              }
                            }}
                          />
                        </Grid>
                      )}
                      <Grid container sx={classes.paddingFooter}>
                        <Button
                          onClick={handleBack}
                          sx={classes.paddingButton}
                          text={BACK_BUTTON}
                          variant="outlined"
                          color="primary"
                        />
                        <Button
                          onClick={(e) => {
                            e.preventDefault();
                            setFieldTouched('client', true);
                            setFieldTouched('clientData', true);

                            if (values.client && values.clientData) {
                              navigate(
                                `/consultation?patient=${values.client}&clientUuid=${values.clientData}`
                              );
                            } else {
                              console.warn(
                                'Select patient type and patient before booking'
                              );
                            }
                          }}
                          className={classes.paddingButton}
                          text={CONTINUE_BUTTON}
                          variant="contained"
                          color="primary"
                          type="button"
                        />
                      </Grid>
                    </div>
                  )}
                </Formik>
              </Container>
            </Paper>
          </Grid>
        )}
      </Container>
    </>
  );
}

export default User;
